import React, { useState } from 'react';
import { Search, CheckCircle, XCircle, AlertCircle, Gift } from 'lucide-react';
import { validateRedemption, fulfillRedemption, RedemptionValidation } from '../services/api';

const DashboardPage: React.FC = () => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validation, setValidation] = useState<RedemptionValidation | null>(null);
  const [error, setError] = useState('');
  const [fulfillSuccess, setFulfillSuccess] = useState(false);

  const handleValidate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setIsLoading(true);
    setError('');
    setValidation(null);
    setFulfillSuccess(false);

    try {
      const result = await validateRedemption(code.trim().toUpperCase());
      setValidation(result);
    } catch (err: any) {
      setError(err.message || 'Failed to validate code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFulfill = async () => {
    if (!validation?.redemption?.redemptionCode) return;

    setIsLoading(true);
    setError('');

    try {
      await fulfillRedemption(validation.redemption.redemptionCode);
      setFulfillSuccess(true);
      setValidation(null);
      setCode('');
    } catch (err: any) {
      setError(err.message || 'Failed to fulfill redemption');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = () => {
    if (!validation) return null;
    if (validation.valid) {
      return <CheckCircle className="w-16 h-16 text-green-500" />;
    }
    if (validation.status === 'already_fulfilled') {
      return <AlertCircle className="w-16 h-16 text-yellow-500" />;
    }
    return <XCircle className="w-16 h-16 text-red-500" />;
  };

  const getStatusColor = () => {
    if (!validation) return 'bg-gray-100';
    if (validation.valid) return 'bg-green-50 border-green-200';
    if (validation.status === 'already_fulfilled') return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-buggy-dark mb-6">Validate Redemption</h1>

      {/* Search Form */}
      <form onSubmit={handleValidate} className="mb-6">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Enter redemption code (e.g., ABC123)"
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-buggy-blue focus:border-transparent outline-none transition uppercase"
              maxLength={10}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !code.trim()}
            className="bg-buggy-blue text-white px-6 py-3 rounded-lg font-medium hover:bg-buggy-blue-light transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Checking...' : 'Validate'}
          </button>
        </div>
      </form>

      {/* Success Message */}
      {fulfillSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-green-700 mb-2">Redemption Fulfilled!</h2>
          <p className="text-green-600">The reward has been marked as fulfilled.</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Validation Result */}
      {validation && (
        <div className={`border rounded-xl p-6 ${getStatusColor()}`}>
          <div className="text-center mb-6">
            {getStatusIcon()}
            <h2 className="text-xl font-bold mt-4 mb-2">
              {validation.valid ? 'Valid Redemption' : validation.message}
            </h2>
            {validation.valid && (
              <p className="text-green-600">Ready to fulfill</p>
            )}
          </div>

          {/* Redemption Details */}
          <div className="bg-white rounded-lg p-4 mb-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-buggy-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Gift className="w-6 h-6 text-buggy-blue" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-buggy-dark">{validation.redemption.reward.name}</h3>
                <p className="text-buggy-gray text-sm">{validation.redemption.reward.description}</p>
                <p className="text-buggy-blue font-medium mt-1">
                  {validation.redemption.pointsRedeemed} points
                </p>
              </div>
            </div>
          </div>

          {/* Member Details */}
          <div className="bg-white rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-buggy-gray mb-2">Member</h4>
            <p className="font-bold text-buggy-dark">
              {validation.redemption.member.firstName} {validation.redemption.member.lastName}
            </p>
            <p className="text-buggy-gray">{validation.redemption.member.phoneNumber}</p>
            <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${
              validation.redemption.member.tier === 'Gold' ? 'bg-yellow-100 text-yellow-800' :
              validation.redemption.member.tier === 'Silver' ? 'bg-gray-100 text-gray-800' :
              'bg-orange-100 text-orange-800'
            }`}>
              {validation.redemption.member.tier} Member
            </span>
          </div>

          {/* Fulfill Button */}
          {validation.valid && (
            <button
              onClick={handleFulfill}
              disabled={isLoading}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Processing...' : 'Mark as Fulfilled'}
            </button>
          )}
        </div>
      )}

      {/* Empty State */}
      {!validation && !error && !fulfillSuccess && (
        <div className="text-center py-12 text-buggy-gray">
          <Search className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>Enter a redemption code to validate</p>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
